import { useEffect, useState } from "react";
import axios from "axios";
import { X, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { WorkerCombobox } from "@/components/WorkerCombobox";
import { Combobox } from "@/components/ui/combobox";
import { HolidayDatesEditor } from "@/components/HolidayDatesEditor";
import { WorkerAvailability } from "@/components/WorkerAvailability";

const empty = {
  worker_id: "",           // existing worker id, or "__new__" for a fresh contractor
  worker_email: "", worker_name: "", worker_phone: "",
  project_id: "",          // existing project id, or "" when the project isn't in the system yet
  auto_assign: true,
  company_name: "", location: "", description: "",
  pay_rate: 0, per_diem_applies: false, per_diem_rate: 0,
  ot_rate_applies: false, ot_rate: 0, ot_threshold_hours: 40,
  holiday_rate_applies: false, holiday_pay_rate: 0, holiday_dates: [],
  contact_name: "", contact_phone: "",
  anticipated_start_date: "TBD", anticipated_duration: "TBD",
};

const NEW_WORKER = "__new__";
const NO_PROJECT = "__none__";

const fromInitial = (i) => i ? {
  ...empty,
  worker_id: i.worker_id || (i.worker_email ? NEW_WORKER : ""),
  worker_email: i.worker_email || "", worker_name: i.worker_name || "", worker_phone: i.worker_phone || "",
  project_id: i.project_id || "",
  auto_assign: i.auto_assign !== false,
  company_name: i.company_name || "", location: i.location || "", description: i.description || "",
  pay_rate: i.pay_rate ?? 0, per_diem_applies: !!i.per_diem_applies, per_diem_rate: i.per_diem_rate ?? 0,
  ot_rate_applies: !!i.ot_rate_applies, ot_rate: i.ot_rate ?? 0, ot_threshold_hours: i.ot_threshold_hours ?? 40,
  holiday_rate_applies: !!i.holiday_rate_applies, holiday_pay_rate: i.holiday_pay_rate ?? 0, holiday_dates: i.holiday_dates || [],
  contact_name: i.contact_name || "", contact_phone: i.contact_phone || "",
  anticipated_start_date: i.anticipated_start_date || "TBD", anticipated_duration: i.anticipated_duration || "TBD",
} : empty;

// mode="sales": a rep drafts the packet (own projects only) → /sales/packets. With autoSend (admin turned off review for this rep)
// it's emailed to the worker immediately; otherwise it waits for admin review.
// `initial` with an id = editing an existing draft; without an id = just pre-fill (e.g. a picked worker).
export default function NewPacketModal({ onClose, onSent, mode = "admin", initial, autoSend = false }) {
  const isSales = mode === "sales";
  const needsReview = isSales && !autoSend;
  const isEdit = !!initial?.id;
  const [f, setF] = useState(fromInitial(initial));
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const active = (r) => (Array.isArray(r.data) ? r.data : []).filter((p) => p.status === "active");
    if (isSales) {
      api.get("/sales/workers").then((r) => setWorkers(Array.isArray(r.data) ? r.data : [])).catch(() => setWorkers([]));
      api.get("/sales/projects").then((r) => setProjects(active(r))).catch(() => setProjects([]));
      return;
    }
    api.get("/workers").then((r) => setWorkers(Array.isArray(r.data) ? r.data : []))
       .catch(() => setWorkers([]));
    api.get("/projects").then((r) => setProjects(active(r)))
       .catch(() => setProjects([]));
    api.get("/clients").then((r) => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => setClients([]));
  }, [isSales]);

  const isExisting = f.worker_id && f.worker_id !== NEW_WORKER;
  const pickedWorker = isExisting ? workers.find((w) => w.id === f.worker_id) : null;
  const hasProject = f.project_id && f.project_id !== NO_PROJECT;
  const project = hasProject ? projects.find((p) => p.id === f.project_id) : null;
  const clientName = (p) => p?.client_name || clients.find((c) => c.id === p?.client_id)?.name || "";

  // Picking a project pre-fills the client-facing details from the Project record so
  // nothing is retyped; pay-side terms stay per-worker.
  const pickProject = (pid) => {
    if (!pid || pid === NO_PROJECT) { setF((p) => ({ ...p, project_id: NO_PROJECT })); return; }
    const p = projects.find((x) => x.id === pid);
    if (!p) return;
    const sup = (p.supervisors || []).find((s) => s?.email) || { name: p.supervisor_name, phone: p.supervisor_phone };
    setF((prev) => ({
      ...prev,
      project_id: pid,
      company_name: clientName(p) || prev.company_name,
      location: p.location || prev.location,
      contact_name: sup?.name || prev.contact_name,
      contact_phone: sup?.phone || prev.contact_phone,
      holiday_dates: (p.holiday_dates || []).length ? p.holiday_dates : prev.holiday_dates,
      holiday_rate_applies: prev.holiday_rate_applies || !!(p.holiday_pay_rate),
      holiday_pay_rate: prev.holiday_pay_rate || p.holiday_pay_rate || 0,
      ot_rate_applies: prev.ot_rate_applies || !!(p.default_ot_pay_rate),
      ot_rate: prev.ot_rate || p.default_ot_pay_rate || 0,
      ot_threshold_hours: p.ot_threshold_hours || prev.ot_threshold_hours || 40,
      pay_rate: prev.pay_rate || p.default_pay_rate || 0,
      per_diem_applies: prev.per_diem_applies || !!(p.default_per_diem),
      per_diem_rate: prev.per_diem_rate || p.default_per_diem || 0,
    }));
  };

  // When admin picks an existing worker, auto-fill the 3 modal-level worker
  // fields (email/name/phone). Assignment details (client, pay rate, dates)
  // are per-project — deliberately NOT auto-filled from stale worker history.
  const pickWorker = (wid) => {
    if (!wid || wid === NEW_WORKER) {
      setF((p) => ({ ...p, worker_id: NEW_WORKER, worker_email: "", worker_name: "", worker_phone: "" }));
      return;
    }
    const w = workers.find((x) => x.id === wid);
    if (!w) return;
    setF((p) => ({
      ...p,
      worker_id: wid,
      worker_email: w.email || "",
      worker_name: w.name || "",
      worker_phone: w.phone || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        ...f,
        // Backend only cares about worker_id when it's a real UUID — send null
        // for "new contractor" so the API creates the packet without linkage.
        worker_id: isExisting ? f.worker_id : null,
        project_id: hasProject ? f.project_id : null,
        auto_assign: hasProject ? !!f.auto_assign : false,
        pay_rate: parseFloat(f.pay_rate) || 0,
        per_diem_rate: f.per_diem_applies ? (parseFloat(f.per_diem_rate) || 0) : 0,
        ot_rate: f.ot_rate_applies ? (parseFloat(f.ot_rate) || 0) : 0,
        ot_threshold_hours: f.ot_rate_applies ? (parseFloat(f.ot_threshold_hours) || 40) : 40,
        holiday_pay_rate: f.holiday_rate_applies ? (parseFloat(f.holiday_pay_rate) || 0) : 0,
        holiday_dates: f.holiday_rate_applies ? (f.holiday_dates || []) : [],
      };
      let data;
      if (isSales) {
        const r = isEdit
          ? await axios.put(`${API_BASE}/sales/packets/${initial.id}`, payload, { withCredentials: true })
          : await axios.post(`${API_BASE}/sales/packets`, payload, { withCredentials: true });
        data = r.data;
        if (data?.status === "sent") {
          if (data.sent === false) toast.error("Packet saved, but the email to the worker could not be delivered — the office has been alerted and can resend.", { duration: 9000 });
          else toast.success(`Assignment packet sent to ${f.worker_email}. The office has been notified.`);
        }
        else toast.success(isEdit ? "Changes submitted for admin review." : "Packet submitted — an admin will review it before it goes to the worker.");
      } else {
        ({ data } = await api.post("/contractor-packets", payload));
        toast.success(`Packet sent to ${f.worker_email}.${data.auto_assign ? " The assignment will be created automatically when they sign." : ""}`);
      }
      onSent?.(data);
      onClose();
    } catch (er) {
      setErr(er.response?.data?.detail?.toString() || er.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 overflow-y-auto"
         data-testid="new-packet-modal" onClick={onClose}>
      <div className="flex items-start sm:items-center justify-center min-h-full p-4">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-md w-full max-w-2xl p-6 border border-slate-200 my-4 sm:my-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="tinylabel">{needsReview ? "Draft for admin review" : isSales ? "Goes straight to the worker" : "Recruit & onboard"}</div>
            <h2 className="text-xl font-black">{isEdit ? "Revise Assignment Packet" : isSales ? "Send Assignment Packet to Worker" : "New Packet / Assignment"}</h2>
            <p className="text-sm text-slate-500 mt-1">
              {needsReview
                ? "Pick one of your projects and the worker. An admin reviews the terms and sends the packet — you'll get an email either way."
                : isSales
                ? "Pick one of your projects and the worker. When you click Send, the worker gets their assignment packet by email right away and the office is notified."
                : "Send an assignment proposal to an existing contractor, or the full onboarding form to a brand-new worker."}
            </p>
            {isSales && isEdit && initial?.review_note && (
              <div className="mt-2 rounded-md border border-orange-300 bg-orange-50 p-2.5 text-sm text-orange-900" data-testid="packet-review-note">
                <b>Admin note:</b> “{initial.review_note}”
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close"><X size={22} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 -mb-1">
            <div className="tinylabel text-slate-600">Worker</div>
            <p className="text-xs text-slate-500 mt-0.5">Pick an existing contractor to pre-fill their info, or choose <b>+ New Contractor</b> for someone we haven't worked with before.</p>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Existing contractor or new</label>
            <WorkerCombobox
              value={f.worker_id}
              onChange={pickWorker}
              workers={workers}
              withNewOption
              testId="packet-worker-picker"
              placeholder="Choose contractor…"
            />
            {isExisting && (
              <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2">
                <b>Returning contractor.</b> Their prior info is on file. When they open the packet, only this
                assignment's details need acceptance — the personal/banking fields are pre-filled and can be
                left as-is unless something has changed.
              </div>
            )}
            {isExisting && isSales && pickedWorker && (
              <div className={`mt-2 text-xs rounded-md px-3 py-2 border ${pickedWorker.current_assignments?.length ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-slate-50 border-slate-200 text-slate-700"}`} data-testid="packet-worker-availability">
                {pickedWorker.status && pickedWorker.status !== "active" && <div className="font-bold mb-1">Currently inactive — they'll be reactivated when they sign.</div>}
                {pickedWorker.current_assignments?.length
                  ? <><div className="font-bold mb-1">Heads up: already on {pickedWorker.current_assignments.length === 1 ? "a job" : `${pickedWorker.current_assignments.length} jobs`} — check availability first.</div>
                      <WorkerAvailability assignments={pickedWorker.current_assignments} compact /></>
                  : <span><b>Not currently assigned</b> — available.</span>}
              </div>
            )}
            {f.worker_id === NEW_WORKER && (
              <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2" data-testid="new-worker-hint">
                <b>New contractor.</b> Enter their <b>email</b> (required), <b>name</b>, and <b>phone</b> in the fields below — the picker above just marks this as a brand-new worker.
              </div>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Worker's email <span className="text-rose-600">*</span></label>
            <input required type="email" value={f.worker_email} onChange={(e) => set("worker_email", e.target.value)}
              data-testid="packet-worker_email" className={`mt-1 w-full h-11 px-3 border border-slate-300 rounded-md ${isExisting ? "bg-slate-50" : ""}`} placeholder="worker@example.com" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Worker's name <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span></label>
            <input value={f.worker_name} onChange={(e) => set("worker_name", e.target.value)}
              data-testid="packet-worker_name" className={`mt-1 w-full h-11 px-3 border border-slate-300 rounded-md ${isExisting ? "bg-slate-50" : ""}`} placeholder="Jane Doe" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Worker's phone <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span></label>
            <input type="tel" value={f.worker_phone} onChange={(e) => set("worker_phone", e.target.value)}
              data-testid="packet-worker_phone" className={`mt-1 w-full h-11 px-3 border border-slate-300 rounded-md ${isExisting ? "bg-slate-50" : ""}`} placeholder="555-123-4567" />
          </div>

          <div className="sm:col-span-2 mt-4 mb-1 pt-4 border-t border-slate-200">
            <div className="tinylabel text-slate-600">Assignment details</div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project</label>
            <Combobox
              value={f.project_id}
              onChange={pickProject}
              options={[
                ...(isSales ? [] : [{ value: NO_PROJECT, label: "Not in the system yet (proposal still out)", selectedLabel: "No project yet — fill in the details below ↓", keywords: ["none", "new", "tbd"], highlight: true, testId: "combobox-project-none" }]),
                ...projects.map((p) => ({ value: p.id, label: p.name, sub: [clientName(p), p.location].filter(Boolean).join(" · "), keywords: [clientName(p), p.location || "", p.po_number || ""] })),
              ]}
              testId="packet-project-picker"
              placeholder={isSales ? "Pick one of your projects…" : "Pick the project this packet is for…"}
              searchPlaceholder="Type project, client or location…"
            />
            {isSales && projects.length === 0 && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="packet-no-projects-hint">
                You don't have an active project yet — packets can only go out on projects credited to you.
              </div>
            )}
            {hasProject && (
              <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5" data-testid="packet-auto-assign-box">
                <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!f.auto_assign} onChange={(e) => set("auto_assign", e.target.checked)}
                    data-testid="packet-auto-assign" className="mt-0.5 h-4 w-4 accent-emerald-600" />
                  <span className="text-emerald-900">
                    <b>Create the assignment automatically</b> when the worker signs &amp; returns this packet — and send their
                    assignment notice (email + text with login details).
                    <span className="block text-xs text-emerald-800 mt-0.5">
                      Bill rate comes from <b>{project?.name}</b>; pay terms below are the worker's. {f.worker_id === NEW_WORKER ? "A Worker record is created from their paperwork too." : ""}
                    </span>
                  </span>
                </label>
              </div>
            )}
            {f.project_id === NO_PROJECT && (
              <div className="mt-2 text-xs text-slate-500">No project linked — you'll create the assignment yourself once the project exists.</div>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Company / Client name</label>
            <input required value={f.company_name} onChange={(e) => set("company_name", e.target.value)}
              data-testid="packet-company_name" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" placeholder="e.g. Turner Construction" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Location</label>
            <input required value={f.location} onChange={(e) => set("location", e.target.value)}
              data-testid="packet-location" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" placeholder="e.g. Columbus, OH — 123 Main St site" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Description of scope</label>
            <textarea required rows={3} value={f.description} onChange={(e) => set("description", e.target.value)}
              data-testid="packet-description" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md" placeholder="Brief description of the work / role" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Hourly pay rate</label>
            <input required type="number" step="0.01" value={f.pay_rate} onChange={(e) => set("pay_rate", e.target.value)}
              data-testid="packet-pay_rate" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
            <div className="mt-1 text-xs text-slate-500">Straight time only.</div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Per diem</label>
            <div className="mt-1 flex items-center gap-2 h-11">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.per_diem_applies}
                  onChange={(e) => set("per_diem_applies", e.target.checked)}
                  data-testid="packet-per_diem_applies" className="h-4 w-4 accent-green-600" />
                Applies
              </label>
              {f.per_diem_applies && (
                <input type="number" step="0.01" value={f.per_diem_rate} onChange={(e) => set("per_diem_rate", e.target.value)}
                  data-testid="packet-per_diem_rate" placeholder="$ / day"
                  className="flex-1 h-9 px-3 border border-slate-300 rounded-md text-sm" />
              )}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Overtime pay</label>
            <div className="mt-1 flex flex-wrap items-center gap-3 min-h-11">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.ot_rate_applies}
                  onChange={(e) => set("ot_rate_applies", e.target.checked)}
                  data-testid="packet-ot_rate_applies" className="h-4 w-4 accent-green-600" />
                Pay overtime above a weekly threshold
              </label>
              {f.ot_rate_applies && (
                <>
                  <div className="inline-flex items-center gap-1">
                    <span className="text-xs text-slate-500">OT pay rate:</span>
                    <input type="number" step="0.01" value={f.ot_rate}
                      onChange={(e) => set("ot_rate", e.target.value)}
                      data-testid="packet-ot_rate" placeholder="$ / hr"
                      className="w-28 h-9 px-3 border border-slate-300 rounded-md text-sm" />
                  </div>
                  <div className="inline-flex items-center gap-1">
                    <span className="text-xs text-slate-500">After:</span>
                    <input type="number" step="0.5" value={f.ot_threshold_hours}
                      onChange={(e) => set("ot_threshold_hours", e.target.value)}
                      data-testid="packet-ot_threshold_hours"
                      className="w-20 h-9 px-3 border border-slate-300 rounded-md text-sm" />
                    <span className="text-xs text-slate-500">hrs / week</span>
                  </div>
                </>
              )}
            </div>
            <div className="mt-1 text-xs text-slate-500">Applies when this worker's weekly hours exceed the threshold. Skip if the assignment is straight-time only.</div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Holiday pay</label>
            <div className="mt-1 flex flex-wrap items-center gap-3 min-h-11">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.holiday_rate_applies}
                  onChange={(e) => set("holiday_rate_applies", e.target.checked)}
                  data-testid="packet-holiday_rate_applies" className="h-4 w-4 accent-green-600" />
                Pay a premium rate for work on designated holidays
              </label>
              {f.holiday_rate_applies && (
                <div className="inline-flex items-center gap-1">
                  <span className="text-xs text-slate-500">Holiday pay rate:</span>
                  <input type="number" step="0.01" value={f.holiday_pay_rate}
                    onChange={(e) => set("holiday_pay_rate", e.target.value)}
                    data-testid="packet-holiday_pay_rate" placeholder="$ / hr"
                    className="w-28 h-9 px-3 border border-slate-300 rounded-md text-sm" />
                </div>
              )}
            </div>
            {f.holiday_rate_applies && (
              <div className="mt-2">
                <div className="text-xs font-semibold text-slate-600 mb-1">Designated holiday dates (shown to the worker on the packet)</div>
                <HolidayDatesEditor value={f.holiday_dates || []} onChange={(v) => set("holiday_dates", v)} idPrefix="packet-holiday" />
              </div>
            )}
            <div className="mt-1 text-xs text-slate-500">Holiday hours are paid in lieu of overtime, not on top of it. Also add these dates to the Project so timesheets pick them up automatically.</div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project contact name</label>
            <input required value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)}
              data-testid="packet-contact_name" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project contact phone</label>
            <input required value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)}
              data-testid="packet-contact_phone" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Anticipated start date</label>
            <input value={f.anticipated_start_date} onChange={(e) => set("anticipated_start_date", e.target.value)}
              data-testid="packet-anticipated_start_date" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" placeholder="TBD or a specific date" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Anticipated duration</label>
            <input value={f.anticipated_duration} onChange={(e) => set("anticipated_duration", e.target.value)}
              data-testid="packet-anticipated_duration" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" placeholder="TBD, ~4 weeks, etc." />
          </div>
        </div>

        {err && <div className="mt-3 text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-md p-3">{err}</div>}

        <div className="mt-6 flex gap-3 justify-end border-t border-slate-100 pt-4">
          <button type="button" onClick={onClose} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
          <button type="submit" disabled={busy} data-testid="packet-send-btn"
                  className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
            <PaperPlaneTilt size={16} weight="bold" />
            {busy ? (isSales && needsReview ? "Submitting…" : "Sending…") : needsReview ? (isEdit ? "Resubmit for review" : "Submit for review") : isSales ? (isEdit ? "Send to worker" : "Send assignment packet") : "Send packet"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
